import {
  ThirdPartyModelTypes,
  ThirdParty,
  MultiModelInfo,
  SingleModelInfo,
  THIRD_PARTY_MODEL_TYPE_INFO,
} from "@/thirdParty/constants";
import {
  THIRD_PARTY_MODEL_TYPES,
  THIRD_PARTY_MODEL_TYPES_INFO,
  THIRD_PARTY_MODEL_TYPE,
} from "@/thirdParty/constants";
import { TranslationDictionary } from "@/types/base";
import { ModelBasicInfo, OptimizerList, OptimizerType } from "@/types/creator";
import { MetaType, NoliboxMetaModelType, Txt2ImgMetaData } from "@/types/meta";
import { Editor_Words } from "@/utils/lang/editor";
import { WordTableNames } from "./components/WordTable/store";
import { AIParamsType } from "./stores/aiParams";

// model type
export type NoliboxModelTypes =
  | "nolibox"
  | "deliberate-general"
  | "epicrealisim-real"
  | "anime"
  | "revanime-3d-anime"
  | "dreamlike";
export const NOLIBOX_MODEL_TYPES: NoliboxModelTypes[] = [
  // "nolibox",
  "deliberate-general",
  "epicrealisim-real",
  "anime",
  "revanime-3d-anime",
  "dreamlike",
];

export type ModelTypes = NoliboxModelTypes | ThirdPartyModelTypes;
export type ModelTypesWithThirdParty = NoliboxModelTypes | ThirdParty;
export type ModelTypesWithMultiThirdParty = NoliboxModelTypes | ThirdParty | ThirdPartyModelTypes;
export interface NoliBoxModelInfo extends ModelBasicInfo<Editor_Words, NoliboxModelTypes> {
  type: "single";
  metaModel: NoliboxMetaModelType;
}

export const MODEL_TYPES = (NOLIBOX_MODEL_TYPES as ModelTypes[]).concat(THIRD_PARTY_MODEL_TYPES);
export const MODEL_TYPES_WITH_THIRD_TYPES = (
  NOLIBOX_MODEL_TYPES as ModelTypesWithThirdParty[]
).concat(THIRD_PARTY_MODEL_TYPE);

const NOLIBOX_MODEL_TYPES_INFO: Record<NoliboxModelTypes, NoliBoxModelInfo> = {
  // anime: {
  //   type: "single",
  //   showInfo: Editor_Words["anime-model-intro"],
  //   value: "anime",
  //   label: Editor_Words["anime-model"],
  //   metaModel: "anime",
  // },
  nolibox: {
    type: "single",
    showInfo: Editor_Words["nolibox-model-intro"],
    value: "nolibox",
    label: Editor_Words["nolibox-model"],
    metaModel: "basic",
  },
  // dreamlike: {
  //   type: "single",
  //   showInfo: Editor_Words["dreamlike-model-intro"],
  //   value: "dreamlike",
  //   label: Editor_Words["dreamlike-model"],
  //   metaModel: "art",
  // },
  "deliberate-general": {
    type: "single",
    showInfo: Editor_Words["deliberate-general"],
    value: "deliberate-general",
    metaModel: "general",
    label: Editor_Words["deliberate-general"],
  },
  "epicrealisim-real": {
    type: "single",
    showInfo: Editor_Words["epicrealisim-real"],
    value: "epicrealisim-real",
    metaModel: "real",
    label: Editor_Words["epicrealisim-real"],
  },
  anime: {
    type: "single",
    showInfo: Editor_Words["anime"],
    value: "anime",
    metaModel: "anime",
    label: Editor_Words["anime"],
  },
  "revanime-3d-anime": {
    type: "single",
    showInfo: Editor_Words["revanime-3d-anime"],
    value: "revanime-3d-anime",
    metaModel: "3danime",
    label: Editor_Words["revanime-3d-anime"],
  },
  dreamlike: {
    type: "single",
    showInfo: Editor_Words["dreamlike"],
    value: "dreamlike",
    metaModel: "art",
    label: Editor_Words["dreamlike"],
  },
};

export const MODEL_TYPES_INFO: Record<NoliboxModelTypes, NoliBoxModelInfo> &
  Record<ThirdPartyModelTypes, SingleModelInfo> = {
  ...NOLIBOX_MODEL_TYPES_INFO,
  ...THIRD_PARTY_MODEL_TYPES_INFO,
};

export const MODEL_TYPES_INFO_WITH_THIRD_PARTY: Record<
  ModelTypesWithThirdParty,
  NoliBoxModelInfo | MultiModelInfo
> = {
  ...NOLIBOX_MODEL_TYPES_INFO,
  ...THIRD_PARTY_MODEL_TYPE_INFO,
};

// mode

export type ModeTypes = "simple" | "advance";
export const MODE_TYPES: ModeTypes[] = ["simple", "advance"];
export const MODE_TYPES_INFO: Record<
  ModeTypes,
  {
    label: Editor_Words;
    value: ModeTypes;
  }
> = {
  simple: {
    label: Editor_Words["mode-simple"],
    value: "simple",
  },
  advance: {
    label: Editor_Words["mode-advance"],
    value: "advance",
  },
};

export const MODEL_WORD_TABLE_SOURCES: Record<
  Extract<
    ModelTypes,
    | "anime"
    | "dreamlike"
    | "nolibox"
    | "deliberate-general"
    | "epicrealisim-real"
    | "revanime-3d-anime"
  >,
  {
    url: string;
    name: WordTableNames;
  }
> = {
  nolibox: {
    url: "https://localimage.oss-cn-hangzhou.aliyuncs.com/noli_creator_static/json/prompt_keywords.json",
    name: "promptKeywords",
  },
  dreamlike: {
    url: "https://localimage.oss-cn-hangzhou.aliyuncs.com/noli_creator_static/json/prompt_keywords.json",
    name: "promptKeywords",
  },
  anime: {
    url: "https://localimage.oss-cn-hangzhou.aliyuncs.com/noli_creator_static/json/anime_keywords.json",
    name: "animeKeywords",
  },
  "deliberate-general": {
    url: "https://localimage.oss-cn-hangzhou.aliyuncs.com/noli_creator_static/json/prompt_keywords.json",
    name: "promptKeywords",
  },
  "epicrealisim-real": {
    url: "https://localimage.oss-cn-hangzhou.aliyuncs.com/noli_creator_static/json/prompt_keywords.json",
    name: "promptKeywords",
  },
  "revanime-3d-anime": {
    url: "https://localimage.oss-cn-hangzhou.aliyuncs.com/noli_creator_static/json/anime_keywords.json",
    name: "animeKeywords",
  },
};

export const optimizerList: OptimizerList = [
  {
    value: "image_quality_improvement",
    content:
      "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text",
  },
  {
    value: "face_beautification",
    content:
      "malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
  },
  {
    value: "posture_correction",
    content:
      "nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy",
  },
  {
    value: "remove_multi_person",
    content: "cloned face, duplicate face, multiple people",
  },
];
export const defaultOptimizers: OptimizerType[] = [
  // "image_quality_improvement",
  "posture_correction",
];
export const modelDefaultOptimizers: Partial<Record<NoliboxModelTypes, OptimizerType[]>> = {
  dreamlike: defaultOptimizers.concat(),
  nolibox: defaultOptimizers.concat(),
  anime: defaultOptimizers.concat(),
};

export const optimizersMap: Record<OptimizerType, { value: string }> = {
  image_quality_improvement: {
    value: "cropped, mutated, error,  username, text",
  },
  face_beautification: {
    value: "disfigured, ugly, fused eyes, unclear eyes, bad face, fused face",
  },
  posture_correction: {
    value:
      "nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts,deformed, mutilated, morbid, signature, watermark，bad anatomy",
  },
  remove_multi_person: { value: "multiple people" },
};

export const optimizerTranlations: TranslationDictionary<OptimizerType> = {
  image_quality_improvement: {
    zh: "画质提升",
    en: "Improve Image Equality",
  },
  face_beautification: {
    zh: "面部美化",
    en: "Beautify Face",
  },
  posture_correction: {
    zh: "结构矫正",
    en: "Correct Posture",
  },
  remove_multi_person: {
    zh: "去除多人",
    en: "Remove Multi-Person",
  },
};

/** presets */
export type PresetTypes = "acg" | "cat";
export const PRESET_TYPES: PresetTypes[] = ["acg", "cat"];
export const PRESETS_TYPE_INFO: Record<
  PresetTypes,
  {
    label: Editor_Words;
    isHot?: boolean;
    value: PresetTypes;
  }
> = {
  acg: {
    label: Editor_Words["preset-type-acg"],
    value: "acg",
    isHot: true,
  },
  cat: {
    label: Editor_Words["preset-type-cat"],
    value: "cat",
  },
};

// 国风 - "Chinese_style" /
// 美漫 - "American_style" /
// 可爱 - "Cute_style" /
// 华丽 - "Gorgeous_style" /
// 恐怖漫画 - "Horrible_style" /
// 精灵 - "Avatar_style" /
// 热血 - "Fighting_style" /
// 机械复古 - "Machine_style" /
// 赛伯未来 - "Cyberfuture_style" /
// JOJO - "JOJO_style"
export type ACG_STYLE_TYPES =
  | "Chinese_style"
  | "American_style"
  | "Cute_style"
  | "Gorgeous_style"
  | "Horrible_style"
  | "Avatar_style"
  | "Fighting_style"
  | "Machine_style"
  | "Cyberfuture_style"
  | "JOJO_style"
  | "Bunny_style"
  | "CatEar_style";

export const ACG_PROMPT_NEGATIVE =
  "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, huge breasts, deformed, mutilated, morbid, bad anatomy";
export const ACG_PROMPT_NEGATIVE_2 =
  "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, huge breasts, deformed, mutilated, morbid, bad anatomy, cloned face, duplicate face, multiple people";

export const ACG_STYLES_LIST: Array<{
  previewUrl: string;
  label: string;
  style: ACG_STYLE_TYPES;
  fidelity?: number;
  negativePrompt?: string;
}> = [
  {
    previewUrl: "https://localimage.nolibox.com/noli_creator_static/Chinese_style.png",
    label: "国风",
    style: "Chinese_style",
    fidelity: 0.2,
  },
  {
    previewUrl: "https://localimage.nolibox.com/noli_creator_static/American_style.png",
    label: "美漫",
    style: "American_style",
    fidelity: 0.3,
  },
  {
    previewUrl: "https://localimage.nolibox.com/noli_creator_static/Cute_style.png",
    label: "可爱",
    style: "Cute_style",
    fidelity: 0.25,
  },
  {
    previewUrl: "https://localimage.nolibox.com/noli_creator_static/Gorgeous_style.png",
    label: "华丽",
    style: "Gorgeous_style",
    fidelity: 0.2,
  },
  {
    previewUrl: "https://localimage.nolibox.com/noli_creator_static/Horrible_style.png",
    label: "恐怖漫画",
    style: "Horrible_style",
    fidelity: 0.15,
  },
  {
    previewUrl: "https://localimage.nolibox.com/noli_creator_static/Avatar_style.png",
    label: "精灵",
    style: "Avatar_style",
    fidelity: 0.2,
  },
  {
    previewUrl: "https://localimage.nolibox.com/noli_creator_static/Fighting_style.png",
    label: "热血",
    style: "Fighting_style",
    fidelity: 0.2,
  },
  {
    previewUrl: "https://localimage.nolibox.com/noli_creator_static/Machine_style.png",
    label: "机械复古",
    style: "Machine_style",
    fidelity: 0.2,
  },
  {
    previewUrl: "https://localimage.nolibox.com/noli_creator_static/Cyberfuture_style.png",
    label: "赛博未来",
    style: "Cyberfuture_style",
    fidelity: 0.15,
  },
  {
    previewUrl: "https://localimage.nolibox.com/noli_creator_static/JOJO_style.png",
    label: "JOJO",
    style: "JOJO_style",
    fidelity: 0.2,
  },
  {
    previewUrl: "https://localimage.nolibox.com/noli_creator_static/Bunny_style.png",
    label: "兔耳",
    style: "Bunny_style",
    fidelity: 0.2,
    negativePrompt: ACG_PROMPT_NEGATIVE_2,
  },
  {
    previewUrl: "https://localimage.nolibox.com/noli_creator_static/CatEar_style.png",
    label: "猫耳",
    style: "CatEar_style",
    fidelity: 0.2,
    negativePrompt: ACG_PROMPT_NEGATIVE_2,
  },
];

// samplers
export const allSDSamplers = [
  "k_euler",
  "k_euler_a",
  "klms",
  "solver",
  "plms",
  "ddim",
  "k_heun",
] as const;
export type SDSampler = typeof allSDSamplers[number];

// AI Params parsable type
export const AI_PARAMS_TYPES: AIParamsType[] = [
  "txt2img",
  "img2img",
  "txt2img.variation",
  "img2img.variation",
  "txt2img.inpainting",
  "inpainting",
  "sr",
  "txt2img.outpainting",
];

export enum NoliboxModelTypeEnum {
  "nolibox" = 1,
  "anime" = 2,
  "dreamlike" = 1,
  "deliberate-general" = 1,
  "epicrealisim-real" = 1,
  "revanime-3d-anime" = 2,
}

export enum NoliboxModelStatusNameEnum {
  "nolibox" = "smallModels",
  "dreamlike" = "smallModels",
  "anime" = "animeSmallModels",
  "deliberate-general" = "smallModels",
  "epicrealisim-real" = "smallModels",
  "revanime-3d-anime" = "animeSmallModels",
}
export const AI_PARAMS_FREE_TYPES: Exclude<MetaType["type"], AIParamsType>[] = [
  "SOD",
  "duplicate",
  "upload",
];

export const DEFAULT_NUM_STEPS = 20;
export const DEFAULT_GUIDANCE_SCALE = 7;

export const MODEL_META_PARAMS_CONFIG: Partial<
  Record<NoliboxMetaModelType | string, Partial<Txt2ImgMetaData>>
> = {
  art: {
    guidance_scale: 7,
    num_steps: 20,
  },
};
